"use client";

import Image from "next/image";
import styles from "./intro-blurb.module.scss";

function IntroBlurb() {
  return (
    <section className={styles.blurbContainer}>
      <h2 className={styles.blurbTitle}>Welcome to PeatSense!</h2>
      <p className={styles.blurbDescription}>
        PeatSense is a collaborative platform aimed at contributing to the
        monitoring, restoration and conservations at peatlands at scale. It is
        developed by the{" "}
        <a href="https://ai2peat.ie/" target="_blank">
          AI2Peat
        </a>{" "}
        project, a project funded by{" "}
        <a href="http://sfi.ie/" target="_blank">
          Science Foundation Ireland
        </a>{" "}
        under its Future Digital Challenge call. The AI2Peat consortium is
        composed of{" "}
        <a href="https://ceadar.ie/" target="_blank">
          CeADAR
        </a>{" "}
        - Ireland&apos;s centre for applied AI -,{" "}
        <a href="https://www.icrag-centre.org/" target="_blank">
          iCRAG
        </a>{" "}
        - the SFI centre for applied geosciences - and{" "}
        <a href="https://www.npws.ie/" target="_blank">
          NPWS
        </a>{" "}
        - Ireland&apos;s National Parks and Wildlife Services.
      </p>

      <h2 className={styles.blurbTitle}>The Importance of Peatlands</h2>
      <p className={styles.blurbDescription}>
        Sustainable peatland management is a major societal challenge in Ireland
        and worldwide. <strong>Peatlands are wetland ecosystems</strong> where,
        under oxygen-poor conditions, partly decayed organic material (plant
        remains) accumulates over thousands of years. Over time, they draw down
        vast amounts of carbon dioxide (CO₂) from the atmosphere and sequester
        the carbon as organic soil.{" "}
        <strong>
          Peatlands provide a range of important ecosystem services to society.
        </strong>{" "}
        By storing and filtering water, they help to minimize pollution of water
        courses, to reduce costs of clean water and to alleviate drought and
        flooding. They support an array of flora and fauna, including many rare
        and especially adapted species. Despite representing only 3% of the
        global land area,{" "}
        <strong>
          peatlands store more carbon than all the world&apos;s forests
        </strong>
        . As societies have developed economically, however, much of
        Earth&apos;s peatland has been damaged or destroyed, thus contributing
        to the global biodiversity and warming crises.
      </p>
      <Image
        src="/map_all_ireland.png"
        className={styles.introImg}
        priority
        width="0"
        height="0"
        sizes="100vw"
        alt="carousel image"
      />
      <p className={styles.blurbDescription}>
        Peat soil covers about 20% of Ireland&apos;s land area; over 90% of this
        is damaged. Pressures include drainage for agriculture, forestry or
        infrastructure development, and extraction for fuel (turf). Although
        Ireland has stopped industrial peat extraction and begun rehabilitating
        some affected sites, this represents a small minority of Irish
        peatlands. In the majority, anthropogenic pressures persist, including
        small-scale extraction, even in legally protected areas. The results are
        habitat loss, water management issues, and largely unquantified
        emissions of CO₂.
      </p>
      <div className={styles.blurbDescription}>
        <p>
          Ireland is under increasing societal, legal and financial pressures
          regarding international commitments to conserve and restore peatlands
          (
          <a
            href="https://environment.ec.europa.eu/topics/nature-and-biodiversity/habitats-directive_en"
            target="_blank"
          >
            EU Habitats Directive
          </a>{" "}
          &amp; upcoming{" "}
          <a
            href="https://environment.ec.europa.eu/topics/nature-and-biodiversity/nature-restoration-law_en"
            target="_blank"
          >
            Nature Restoration Law
          </a>
          ), to maintain and improve water quality (
          <a
            href="https://environment.ec.europa.eu/topics/water/water-framework-directive_en"
            target="_blank"
          >
            EU Water Framework Directive
          </a>
          ), and to estimate and report reliable national greenhouse gas
          emissions (GHG) from peatlands (
          <a
            href="https://www.un.org/en/climatechange/paris-agreement"
            target="_blank"
          >
            UN Paris Agreement
          </a>
          ). Management challenges are further complicated by the sensitivity of
          the rainwater-dependent Irish peatlands to climate change.
          Opportunities for Ireland include:
        </p>
        <ol className={styles.blurbOrderedList}>
          <li className={styles.blurbOrderedListItem}>
            achievement of national climate action and biodiversity goals and
            international commitments for the benefit of future generations.
          </li>
          <li className={styles.blurbOrderedListItem}>
            realisation of a socially-just transition to new or more sustainable
            economic activities and opportunities (green energy, carbon credits,
            tourism, etc.).
          </li>
          <li className={styles.blurbOrderedListItem}>
            world-leadership in data-driven and community-orientated peatland
            management.
          </li>
        </ol>
      </div>
    </section>
  );
}

export default IntroBlurb;
